import { Inbox } from "lucide-react";
import * as React from "react";

import {
  Badge,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "./base";
import { cn, formatarCentavos } from "./utils";

type Tom = "neutro" | "sucesso" | "oficial" | "alerta" | "erro";

// ─── Tipo e status do número ─────────────────────────────────────────────────

/** Oficial = azul frio; Não oficial = esmeralda. Sempre cor + rótulo. */
export function TipoNumeroTag({ tipo, className }: { tipo: "OFICIAL" | "NAO_OFICIAL"; className?: string }) {
  return tipo === "OFICIAL" ? (
    <Badge tom="oficial" className={className}>
      API Oficial
    </Badge>
  ) : (
    <Badge tom="sucesso" className={className}>
      Não oficial
    </Badge>
  );
}

const STATUS_NUMERO: Record<string, { rotulo: string; tom: Tom; pulsa?: boolean }> = {
  CONECTADO: { rotulo: "Conectado", tom: "sucesso" },
  ATIVO: { rotulo: "Ativo", tom: "oficial" },
  CONECTANDO: { rotulo: "Conectando", tom: "alerta", pulsa: true },
  AGUARDANDO_WEBHOOK: { rotulo: "Aguardando webhook", tom: "alerta", pulsa: true },
  DESCONECTADO: { rotulo: "Desconectado", tom: "neutro" },
  ERRO: { rotulo: "Erro", tom: "erro" },
  RESTRITO: { rotulo: "Restrito", tom: "alerta" },
  DESATIVADO: { rotulo: "Desativado", tom: "erro" },
  REMOCAO_PENDENTE: { rotulo: "Removendo", tom: "neutro", pulsa: true },
};

export function StatusNumeroBadge({ status, className }: { status: string; className?: string }) {
  const s = STATUS_NUMERO[status] ?? { rotulo: status, tom: "neutro" as Tom };
  return (
    <Badge tom={s.tom} className={className}>
      <span
        aria-hidden="true"
        className={cn("size-1.5 rounded-full bg-current", s.pulsa && "zh-pulso text-current")}
      />
      {s.rotulo}
    </Badge>
  );
}

const STATUS_LEAD: Record<string, { rotulo: string; tom: Tom }> = {
  NOVO: { rotulo: "Novo", tom: "oficial" },
  REPASSADO: { rotulo: "Repassado", tom: "alerta" },
  EM_CONTATO: { rotulo: "Em contato", tom: "alerta" },
  RESPONDEU: { rotulo: "Respondeu", tom: "sucesso" },
  SEM_RESPOSTA: { rotulo: "Sem resposta", tom: "neutro" },
  DESCARTADO: { rotulo: "Descartado", tom: "neutro" },
};
export function StatusLeadBadge({ status }: { status: string }) {
  const s = STATUS_LEAD[status] ?? { rotulo: status, tom: "neutro" as Tom };
  return <Badge tom={s.tom}>{s.rotulo}</Badge>;
}

// ─── Uso do plano ("2 de 3") ─────────────────────────────────────────────────

export function UsoDoPlano({ usados, limite, className }: { usados: number; limite: number; className?: string }) {
  const cheio = limite > 0 && usados >= limite;
  return (
    <div className={cn("flex items-center gap-3", className)}>
      <span className="num-tab text-sm">
        <strong className="text-base">{usados}</strong> de {limite}
      </span>
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={limite}
        aria-valuenow={usados}
        aria-label="Números usados do plano"
        className="flex gap-1"
      >
        {Array.from({ length: Math.max(limite, 1) }, (_, i) => (
          <span
            key={i}
            className={cn(
              "h-2 w-5 rounded-sm",
              i < usados ? (cheio ? "bg-warning" : "bg-primary") : "bg-muted",
            )}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Estado vazio ────────────────────────────────────────────────────────────

export function EmptyState({
  titulo,
  descricao,
  acao,
  icone,
  className,
}: {
  titulo: string;
  descricao?: string;
  acao?: React.ReactNode;
  icone?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-col items-center gap-3 rounded-lg border border-dashed p-10 text-center",
        className,
      )}
    >
      <div className="grid size-12 place-items-center rounded-md border bg-muted text-muted-foreground">
        {icone ?? <Inbox className="size-5" />}
      </div>
      <h3 className="font-display text-base font-semibold">{titulo}</h3>
      {descricao && <p className="max-w-sm text-sm text-muted-foreground">{descricao}</p>}
      {acao}
    </div>
  );
}

// ─── Confirmação destrutiva (digitar o apelido) ──────────────────────────────

export function ConfirmacaoDestrutiva({
  aberto,
  aoMudar,
  titulo,
  descricao,
  frase,
  rotuloAcao = "Remover",
  aoConfirmar,
  carregando,
}: {
  aberto: boolean;
  aoMudar: (aberto: boolean) => void;
  titulo: string;
  descricao: string;
  /** O usuário precisa digitar exatamente isto (ex.: o apelido do número). */
  frase: string;
  rotuloAcao?: string;
  aoConfirmar: () => void;
  carregando?: boolean;
}) {
  const [digitado, setDigitado] = React.useState("");
  React.useEffect(() => {
    if (!aberto) setDigitado("");
  }, [aberto]);
  const ok = digitado.trim() === frase;
  return (
    <Dialog open={aberto} onOpenChange={aoMudar}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{titulo}</DialogTitle>
          <DialogDescription>{descricao}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-2">
          <Label htmlFor="zh-confirmacao">
            Digite <span className="num-tab rounded-sm bg-muted px-1.5 py-0.5">{frase}</span> para confirmar
          </Label>
          <Input
            id="zh-confirmacao"
            value={digitado}
            onChange={(e) => setDigitado(e.target.value)}
            autoComplete="off"
          />
        </div>
        <DialogFooter>
          <Button variante="contorno" onClick={() => aoMudar(false)}>
            Cancelar
          </Button>
          <Button variante="perigo" disabled={!ok || carregando} onClick={aoConfirmar}>
            {carregando ? "Aguarde…" : rotuloAcao}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Métrica ─────────────────────────────────────────────────────────────────

export function MetricCard({
  rotulo,
  valor,
  detalhe,
  className,
}: {
  rotulo: string;
  valor: React.ReactNode;
  detalhe?: React.ReactNode;
  className?: string;
}) {
  return (
    <Card className={cn("p-5", className)}>
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{rotulo}</p>
      <p className="num-tab mt-2 text-3xl font-semibold">{valor}</p>
      {detalhe && <p className="mt-1 text-xs text-muted-foreground">{detalhe}</p>}
    </Card>
  );
}

// ─── Contador de economia (hodômetro Utility–Marketing) ──────────────────────

function useContagem(alvo: number, animar: boolean): number {
  const [valor, setValor] = React.useState(alvo);
  const anterior = React.useRef(alvo);
  React.useEffect(() => {
    const semMovimento =
      typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (!animar || semMovimento) {
      setValor(alvo);
      anterior.current = alvo;
      return;
    }
    const de = anterior.current;
    const inicio = performance.now();
    const duracao = 900;
    let quadro = 0;
    const passo = (agora: number) => {
      const t = Math.min(1, (agora - inicio) / duracao);
      const suave = 1 - Math.pow(1 - t, 3);
      setValor(Math.round(de + (alvo - de) * suave));
      if (t < 1) quadro = requestAnimationFrame(passo);
      else anterior.current = alvo;
    };
    quadro = requestAnimationFrame(passo);
    return () => cancelAnimationFrame(quadro);
  }, [alvo, animar]);
  return valor;
}

/**
 * "Você economizou usando a API não oficial" — dois valores lado a lado
 * (pelo preço Utility e pelo Marketing): a economia real fica nessa faixa.
 */
export function ContadorEconomia({
  utilityCentavos,
  marketingCentavos,
  periodo,
  animar = true,
  className,
}: {
  utilityCentavos: number;
  marketingCentavos: number;
  periodo?: string;
  animar?: boolean;
  className?: string;
}) {
  const u = useContagem(utilityCentavos, animar);
  const m = useContagem(marketingCentavos, animar);
  return (
    <Card className={cn("tick p-6", className)} aria-live="polite">
      <p className="text-sm text-muted-foreground">
        Você economizou usando a API não oficial{periodo ? ` · ${periodo}` : ""}
      </p>
      {/* flex-wrap, não grid por breakpoint: o card vive em colunas estreitas e valores grandes quebram de linha. */}
      <div className="mt-4 flex flex-wrap gap-x-8 gap-y-4">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Pelo preço Utility</p>
          <p className="num-tab mt-1 whitespace-nowrap text-3xl font-semibold text-primary sm:text-4xl">{formatarCentavos(u)}</p>
        </div>
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Pelo preço Marketing</p>
          <p className="num-tab mt-1 whitespace-nowrap text-3xl font-semibold text-primary sm:text-4xl">{formatarCentavos(m)}</p>
        </div>
      </div>
      <p className="mt-4 text-xs text-muted-foreground">
        A economia real fica entre os dois valores, conforme o tipo de mensagem que você enviaria pela API Oficial.
      </p>
    </Card>
  );
}

// ─── Fio de sinal (âncora visual) ────────────────────────────────────────────

/** Passos ligados por um fio fino com nós: QR → Conectado → Mensagem → Economia. */
export function FioDeSinal({
  passos,
  ativo = 0,
  className,
}: {
  passos: Array<{ titulo: string; detalhe?: string }>;
  ativo?: number;
  className?: string;
}) {
  return (
    <ol className={cn("relative grid gap-6", className)}>
      {passos.map((p, i) => (
        <li key={p.titulo} className="relative flex gap-4">
          {i < passos.length - 1 && (
            <span aria-hidden="true" className="absolute left-[11px] top-6 h-[calc(100%+0.5rem)] w-px bg-border" />
          )}
          <span
            aria-hidden="true"
            className={cn(
              "z-10 mt-0.5 grid size-6 shrink-0 place-items-center rounded-full border text-[10px] num-tab",
              i < ativo && "border-primary bg-primary text-primary-foreground",
              i === ativo && "zh-pulso border-primary bg-background text-primary",
              i > ativo && "bg-background text-muted-foreground",
            )}
          >
            {i + 1}
          </span>
          <div>
            <p className="font-display text-sm font-semibold">{p.titulo}</p>
            {p.detalhe && <p className="text-sm text-muted-foreground">{p.detalhe}</p>}
          </div>
        </li>
      ))}
    </ol>
  );
}

/** Aviso exigido pela licença do Evolution Go: texto único, usado no painel, no site e na plataforma. */
export const AVISO_EVOLUTION_GO = "As conexões não oficiais do ZapHub são operadas com Evolution Go (Evolution Foundation).";

/**
 * Aviso de uso do Evolution Go (licença). Só texto: a marca e o logo do Evolution não aparecem
 * em outras telas, conforme a política de marcas dele.
 */
export function AvisoEvolution({ className }: { className?: string }) {
  return <p className={cn("text-xs text-muted-foreground", className)}>{AVISO_EVOLUTION_GO}</p>;
}
